import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, FileText, Wallet, CalendarOff, AlertTriangle } from "lucide-react";

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  inactive: "bg-stone-100 text-stone-700 border-stone-200",
  suspended: "bg-rose-100 text-rose-800 border-rose-200",
  unavailable: "bg-amber-100 text-amber-800 border-amber-200",
};
const DRIVER_STATUSES = ["active", "inactive", "suspended", "unavailable"];
const BLOCK_TYPES = ["day_off", "sick_leave", "vacation", "unavailable", "suspended"];
const DOC_TYPES = ["license", "professional_license", "accreditation", "company_authorization", "medical_fitness", "training_certificate"];
const COMP_RATE_TYPES = ["fixed_daily", "fixed_per_booking", "hourly", "monthly", "custom"];

export default function DriverProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compensation, setCompensation] = useState(null); // null = not loaded/no access, [] = loaded empty
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [showCompForm, setShowCompForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/drivers/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get(`/drivers/${id}/compensation`)
      .then((r) => setCompensation(r.data))
      .catch(() => setCompensation(null)); // 403 (no finances perm): stays hidden, no error toast
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!data) return <div className="text-slate-500">Driver not found.</div>;

  const { driver, upcoming_bookings, recent_bookings, availability_blocks, documents, incidents } = data;

  const setStatus = async (newStatus) => {
    try {
      await api.put(`/drivers/${id}/status`, { driver_status: newStatus });
      toast.success("Driver status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="driver-profile-page">
      <Link to="/app/drivers" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Driver Administration
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold" data-testid="driver-profile-name">
            {driver.driver_display_name || driver.name}
          </h1>
          <p className="text-slate-600 mt-1">{driver.driver_phone || driver.phone || "No phone on file"}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-3 py-1 rounded-full font-medium border ${STATUS_TONE[driver.driver_status] || STATUS_TONE.active}`}>
            {driver.driver_status || "active"}
          </span>
          <select value={driver.driver_status || "active"} onChange={(e) => setStatus(e.target.value)}
                  data-testid="driver-status-select"
                  className="text-xs px-2 py-1.5 rounded-lg border border-slate-300">
            {DRIVER_STATUSES.map((s) => <option key={s} value={s}>Set: {s}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Section title="License & identity">
          <Row label="License number" value={driver.license_number} />
          <Row label="License class" value={driver.license_class} />
          <Row label="License expiry" value={driver.license_expiry ? String(driver.license_expiry).slice(0, 10) : null} />
          <Row label="Notes" value={driver.driver_notes} />
        </Section>

        <Section title="Upcoming assignments">
          {(upcoming_bookings || []).length === 0 && <Empty text="No upcoming assignments." />}
          {(upcoming_bookings || []).map((b) => (
            <Link key={b.id} to={`/app/bookings/${b.id}`} className="block text-sm py-1.5 border-b border-slate-100 last:border-0 hover:text-[#2D6A4F]">
              <div className="font-medium">{b.booking_reference}: {b.customer_name}</div>
              <div className="text-xs text-slate-500">{String(b.start_datetime).slice(0, 16).replace("T", " ")}</div>
            </Link>
          ))}
        </Section>

        <Section title="Recent completed bookings">
          {(recent_bookings || []).length === 0 && <Empty text="No completed bookings yet." />}
          {(recent_bookings || []).map((b) => (
            <Link key={b.id} to={`/app/bookings/${b.id}`} className="block text-sm py-1.5 border-b border-slate-100 last:border-0 hover:text-[#2D6A4F]">
              <div className="font-medium">{b.booking_reference}: {b.customer_name}</div>
              <div className="text-xs text-slate-500">{String(b.end_datetime).slice(0, 10)}</div>
            </Link>
          ))}
        </Section>
      </div>

      <Section title="Availability & leave" icon={CalendarOff}
                action={<SmallButton onClick={() => setShowBlockForm(true)} testId="driver-add-block-btn"><Plus className="w-3 h-3" /> Add block</SmallButton>}>
        {(availability_blocks || []).length === 0 && <Empty text="No active availability blocks." />}
        <div className="space-y-1.5">
          {(availability_blocks || []).map((blk) => (
            <div key={blk.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`avail-block-${blk.id}`}>
              <div>
                <span className="font-medium capitalize">{blk.block_type.replace("_", " ")}</span>
                <span className="text-slate-500 ml-2">
                  {String(blk.start_at).slice(0, 16).replace("T", " ")} → {String(blk.end_at).slice(0, 16).replace("T", " ")}
                </span>
                {blk.note && <div className="text-xs text-slate-500">{blk.note}</div>}
              </div>
              <button onClick={async () => {
                await api.delete(`/drivers/${id}/availability-blocks/${blk.id}`);
                toast.success("Block cancelled");
                load();
              }} className="text-xs text-rose-600 hover:underline">Cancel</button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Documents" icon={FileText}
                action={<SmallButton onClick={() => setShowDocForm(true)} testId="driver-add-doc-btn"><Plus className="w-3 h-3" /> Upload</SmallButton>}>
        {(documents || []).length === 0 && <Empty text="No documents uploaded." />}
        <div className="space-y-1.5">
          {(documents || []).map((doc) => (
            <div key={doc.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`driver-doc-${doc.id}`}>
              <div>
                <span className="font-medium">{doc.title || doc.document_type}</span>
                <span className="text-slate-500 ml-2 capitalize">{doc.document_type.replace(/_/g, " ")}</span>
                {doc.expiry_date && <div className="text-xs text-slate-500">Expires {String(doc.expiry_date).slice(0, 10)}</div>}
              </div>
              <button onClick={async () => {
                if (!window.confirm("Delete this document?")) return;
                await api.delete(`/drivers/${id}/documents/${doc.id}`);
                toast.success("Document deleted");
                load();
              }} className="text-xs text-rose-600 hover:underline">Delete</button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Compensation" icon={Wallet}
                action={compensation !== null && <SmallButton onClick={() => setShowCompForm(true)} testId="driver-add-comp-btn"><Plus className="w-3 h-3" /> Add rate</SmallButton>}>
        {compensation === null && (
          <p className="text-sm text-slate-500" data-testid="driver-comp-hidden">
            You don't have permission to view compensation for this driver. Requires the "Finance &amp; compensation records" permission.
          </p>
        )}
        {compensation !== null && compensation.length === 0 && <Empty text="No compensation rate recorded yet." />}
        {compensation !== null && compensation.map((c) => (
          <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`driver-comp-${c.id}`}>
            <div>
              <span className="font-medium capitalize">{c.rate_type.replace(/_/g, " ")}</span>
              <span className="text-slate-600 ml-2">{c.currency} {c.amount}</span>
              {!c.active && <span className="ml-2 text-xs text-slate-400">(superseded)</span>}
              <div className="text-xs text-slate-500">Effective {c.effective_date}</div>
            </div>
          </div>
        ))}
      </Section>

      {(incidents || []).length > 0 && (
        <Section title="Linked incidents" icon={AlertTriangle}>
          <p className="text-xs text-slate-500 mb-2">Operational history only: being assigned to a booking does not imply fault.</p>
          {incidents.map((inc) => (
            <Link key={inc.id} to={`/app/incidents/${inc.id}`} className="block text-sm py-1.5 border-b border-slate-100 last:border-0 hover:text-[#2D6A4F]">
              {inc.incident_type}: {inc.status}
            </Link>
          ))}
        </Section>
      )}

      {showBlockForm && <AvailabilityBlockModal driverId={id} onClose={() => setShowBlockForm(false)} onSaved={() => { setShowBlockForm(false); load(); }} />}
      {showDocForm && <DocumentModal driverId={id} onClose={() => setShowDocForm(false)} onSaved={() => { setShowDocForm(false); load(); }} />}
      {showCompForm && <CompensationModal driverId={id} onClose={() => setShowCompForm(false)} onSaved={() => { setShowCompForm(false); load(); }} />}
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />} {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function SmallButton({ onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 hover:bg-slate-50">
      {children}
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium">{value || "-"}</span>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

function ModalShell({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid={testId}>
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6 my-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">{title}</h2>
            <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function AvailabilityBlockModal({ driverId, onClose, onSaved }) {
  const [form, setForm] = useState({ block_type: "day_off", start_at: "", end_at: "", note: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/drivers/${driverId}/availability-blocks`, {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      });
      toast.success("Availability block added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell title="Add availability block" onClose={onClose} testId="avail-block-modal">
      <form onSubmit={submit} className="space-y-3">
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Type</span>
          <select value={form.block_type} onChange={(e) => setForm({ ...form, block_type: e.target.value })}
                  data-testid="block-form-type"
                  className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300">
            {BLOCK_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Starts</span>
          <input type="datetime-local" required value={form.start_at} data-testid="block-form-start"
                 onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Ends</span>
          <input type="datetime-local" required value={form.end_at} data-testid="block-form-end"
                 onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Note (optional)</span>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="block-form-submit" className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60">
            {saving ? "Saving…" : "Add block"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DocumentModal({ driverId, onClose, onSaved }) {
  const [form, setForm] = useState({ document_type: "license", title: "", issue_date: "", expiry_date: "", reminder_date: "", notes: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/^(application\/pdf|image\/(jpeg|png|webp))$/.test(f.type)) {
      toast.error("Only PDF, JPG, PNG, or WEBP files are supported");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFile({ data_base64: reader.result.split(",")[1], content_type: f.type });
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a file"); return; }
    setSaving(true);
    try {
      await api.post(`/drivers/${driverId}/documents`, { ...form, ...file });
      toast.success("Document uploaded");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Upload driver document" onClose={onClose} testId="driver-doc-modal">
      <form onSubmit={submit} className="space-y-3">
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Document type</span>
          <select value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })}
                  data-testid="doc-form-type"
                  className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300">
            {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Title (optional)</span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Issue date</span>
            <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                   className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
          </label>
          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Expiry date</span>
            <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                   className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
          </label>
        </div>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">File (PDF/JPG/PNG/WEBP, max 8MB)</span>
          <input type="file" required data-testid="doc-form-file" accept="application/pdf,image/jpeg,image/png,image/webp"
                 onChange={(e) => handleFile(e.target.files?.[0])}
                 className="mt-1.5 w-full text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="doc-form-submit" className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60">
            {saving ? "Uploading…" : "Upload"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function CompensationModal({ driverId, onClose, onSaved }) {
  const [form, setForm] = useState({ rate_type: "fixed_daily", amount: "", currency: "PHP", effective_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/drivers/${driverId}/compensation`, { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Compensation rate recorded");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell title="Set driver compensation" onClose={onClose} testId="driver-comp-modal">
      <form onSubmit={submit} className="space-y-3">
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Rate type</span>
          <select value={form.rate_type} onChange={(e) => setForm({ ...form, rate_type: e.target.value })}
                  data-testid="comp-form-rate-type"
                  className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300">
            {COMP_RATE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Amount</span>
            <input type="number" step="0.01" required value={form.amount} data-testid="comp-form-amount"
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
          </label>
          <label>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Currency</span>
            <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                   className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
          </label>
        </div>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Effective date</span>
          <input type="date" required value={form.effective_date}
                 onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes (optional)</span>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                 className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
        </label>
        <p className="text-xs text-slate-500">Internal pay reference only, never shown to customers.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="comp-form-submit" className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60">
            {saving ? "Saving…" : "Save rate"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
